import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDate } from "src/utils/dateTime";
import { DateRangePicker } from "src/view/conponents/DateRangePicker";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Input, { InputBoxStyle, InputFontStyle } from "@ui/input/Input";
import { Select } from "@ui/select/Select";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { Table } from "@ui/table/Table";
import { Typo, TypoCaption } from "@ui/text/Typo";
import { ChevronDown, ChevronUp, Download, Info, Lock, Percent, TrendingUp, Unlock, Wallet } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { GD } from "src/GD";
import { useAPI } from "src/hooks/useApi";
import { formatAmount, saveSummaryPdf } from "src/tools/tools";
import type { SummaryPDFRow } from "src/tools/pdf/SummaryPDF";
import { IPagination, Pagination } from "../../conponents/Pagination";
import { NoRecords } from "../../providers/NoRecords";
import { S_REPORT_POPUP } from "../../reports/ReportPopup";
import { saveAs } from "file-saver";
import { getNotificationErrorMessage, notify } from "src/tools/notify";
import { buildReportsCsv, loadReportsForCsv, type ReportsExportFilters } from "./reportsCsv";

type TReportTotals = {
	total_net: number;
	total_fees: number;
	total_amount: number;
	count_failed: number;
	count_refunds: number;
	count_chargebacks: number;
	count_oct: number;
	count_success: number;
	total_refunds: number;
	total_chargebacks: number;
	total_oct: number;
	total_rollings: number;
	count_ajustment_in: number;
	total_ajustment_in: number;
	count_ajustment_out: number;
	total_ajustment_out: number;
	total_release_rollings: number;
};

type TReportClientData = {
	id: number;
	name: string;
	totals?: Record<string, TReportTotals>;
	merchants?: { id: number; name: string }[];
};

type TReportStatus = "created" | "pending" | "payed";

type TReportItem = {
	id: number;
	created_at: string;
	calculated_at: string;
	project_id: number;
	client_id: number;
	data: TReportClientData;
	date: string;
	status?: string | null;
	generated_in: number;
	report: any;
};

// Mirrors the Sessions pill palette: completed-style for `payed`, amber for `pending`,
// neutral slate for `created`. Label is the user-facing version.
const REPORT_STATUS_VISUALS: Record<TReportStatus, { bg: string; text: string; label: string }> = {
	payed: { bg: "#d6ecdd", text: "#14532d", get label() { return msg("paid"); } },
	pending: { bg: "#fef3c7", text: "#92400e", get label() { return msg("pending"); } },
	created: { bg: "#f1f5f9", text: "#475569", get label() { return msg("created"); } },
};

// ── reports/get summary (per-status, per-currency aggregate from backend) ────────────────
type TReportSummaryMetrics = { turnover: number; fee: number; rollings: number; released: number; net: number };
type TReportStatusSummary = { count: number; from?: string | null; to?: string | null; currencies: Record<string, TReportSummaryMetrics> };
type TReportsSummary = Record<TReportStatus, TReportStatusSummary>;

// lifecycle order; pending (awaiting payout) first as the most actionable bucket
const REPORT_SUMMARY_STATUS_ORDER: TReportStatus[] = ["pending", "created", "payed"];

// metric → label + lucide icon + accent (icon-square accent only, rest neutral) — mirrors Sessions
const REPORT_SUMMARY_METRICS: { key: keyof TReportSummaryMetrics; label: string; icon: typeof TrendingUp; accent: string; bg: string }[] = [
	{ key: "turnover", get label() { return msg("turnover_8ecaf1d7"); }, icon: TrendingUp, accent: "#0f766e", bg: "#f0fdfa" },
	{ key: "net", get label() { return msg("net"); }, icon: Wallet, accent: "#15803d", bg: "#ecfdf5" },
	{ key: "fee", get label() { return msg("fee"); }, icon: Percent, accent: "#854d0e", bg: "#fefce8" },
	{ key: "rollings", get label() { return msg("rollings_bd316df6"); }, icon: Lock, accent: "#9a3412", bg: "#fff7ed" },
	{ key: "released", get label() { return msg("released_f33deea9"); }, icon: Unlock, accent: "#1e40af", bg: "#eff6ff" },
];

const fmtSummaryMoney = (v: number): string => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ReportSummaryItem = ({
	label,
	amount,
	currencyPrefix,
	icon: Icon,
	accent,
	bg,
}: {
	label: string;
	amount: string;
	currencyPrefix: string;
	icon: typeof TrendingUp;
	accent: string;
	bg: string;
}) => { useLocale(); return ((
	<HBox gap="10px" sx={{ minWidth: 0, alignItems: "center" }}>
		<Box
			sx={{
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				width: "32px",
				height: "32px",
				borderRadius: "8px",
				backgroundColor: bg,
				color: accent,
				flexShrink: 0,
			}}>
			<Icon size={16} strokeWidth={2.25} />
		</Box>
		<VBox sx={{ gap: "1px", minWidth: 0 }}>
			<Typo
				sx={{
					fontSize: "10px",
					color: "#64748b",
					textTransform: "uppercase",
					letterSpacing: "0.06em",
					fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
					fontWeight: 500,
					whiteSpace: "nowrap",
				}}>
				{label}
			</Typo>
			<Typo sx={{ fontSize: "14px", fontWeight: 600, color: "#1e293b", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
				<span style={{ color: "#94a3b8", fontWeight: 400, marginRight: "2px" }}>{currencyPrefix}</span>
				{amount}
			</Typo>
		</VBox>
	</HBox>
)); };

// Flatten the per-status / per-currency summary into SummaryPDF rows: a section header per
// status (with report count) followed by its metric rows; Net highlighted as the bottom line.
const buildReportsSummaryPdfRows = (summary: TReportsSummary): SummaryPDFRow[] => {
	const rows: SummaryPDFRow[] = [];
	for (const status of REPORT_SUMMARY_STATUS_ORDER) {
		const bucket = summary[status];
		if (!bucket || bucket.count <= 0) continue;
		const period = bucket.from && bucket.to ? (bucket.from === bucket.to ? `  ·  ${bucket.from}` : `  ·  ${bucket.from} → ${bucket.to}`) : "";
		rows.push({ label: `${REPORT_STATUS_VISUALS[status].label}${period}`, count: bucket.count, section: true });
		const currencies = Object.keys(bucket.currencies);
		for (const cur of currencies) {
			const m = bucket.currencies[cur];
			const suffix = currencies.length > 1 ? ` (${cur})` : "";
			rows.push({ label: msg("templateTurnover_55181640", { v0: suffix }), amount: fmtSummaryMoney(m.turnover), currencyPrefix: cur });
			rows.push({ label: msg("templateFee_3b0bf83a", { v0: suffix }), amount: fmtSummaryMoney(m.fee), currencyPrefix: cur });
			rows.push({ label: msg("templateRollings_8da9ebe3", { v0: suffix }), amount: fmtSummaryMoney(m.rollings), currencyPrefix: cur });
			rows.push({ label: msg("templateReleased_2eccbc62", { v0: suffix }), amount: fmtSummaryMoney(m.released), currencyPrefix: cur });
			rows.push({ label: msg("templateNet_3aa01dc3", { v0: suffix }), amount: fmtSummaryMoney(m.net), currencyPrefix: cur, highlight: true });
		}
	}
	return rows;
};

// Shown when the backend suppressed the summary because the date window exceeds ~2 months
// (summary comes back as null only in that case for a list response).
const ReportsSummaryHint = () => { useLocale(); return ((
	<HBox
		sx={{
			background: "#fff",
			borderRadius: "14px",
			boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 2px 12px rgba(0,0,0,0.03)",
			padding: "12px 20px",
			gap: "10px",
			alignItems: "center",
		}}>
		<Box
			sx={{
				display: "inline-flex",
				alignItems: "center",
				justifyContent: "center",
				width: "28px",
				height: "28px",
				borderRadius: "8px",
				backgroundColor: "#f1f5f9",
				color: "#64748b",
				flexShrink: 0,
			}}>
			<Info size={15} strokeWidth={2.25} />
		</Box>
		<Typo sx={{ fontSize: "12px", color: "#64748b" }}>{msg("summaryIsAvailableForPeriodsUpTo2MonthsNarrowTheDateRangeToSeeIt")}</Typo>
	</HBox>
)); };

const ReportsSummaryPanel = ({
	summary,
	from,
	to,
	collapsed,
	onToggleCollapse,
}: {
	summary: TReportsSummary;
	from?: string;
	to?: string;
	collapsed?: boolean;
	onToggleCollapse?: () => void;
}) => {
	useLocale();
	const visible = REPORT_SUMMARY_STATUS_ORDER.filter(s => summary[s] && summary[s].count > 0);
	if (visible.length === 0) return null;

	return (
		<VBox
			sx={{
				background: "#fff",
				borderRadius: "14px",
				boxShadow: "0 1px 4px rgba(0,0,0,0.04), 0 2px 12px rgba(0,0,0,0.03)",
				padding: "14px 20px",
				gap: "14px",
			}}>
			<HBox sx={{ justifyContent: "space-between", alignItems: "center", minHeight: "16px" }}>
				<Typo
					sx={{
						fontSize: "10px",
						color: "#94a3b8",
						textTransform: "uppercase",
						letterSpacing: "0.08em",
						fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
						fontWeight: 600,
					}}>{msg("summary")}</Typo>
				<HBox gap="4px" sx={{ alignItems: "center" }}>
					<Box
						onClick={() =>
							saveSummaryPdf({
								title: msg("reportsSummary"),
								periodFrom: from || "all",
								periodTo: to || "all",
								rows: buildReportsSummaryPdfRows(summary),
							})
						}
						title={msg("downloadSummaryAsPdf")}
						sx={{
							display: "inline-flex",
							alignItems: "center",
							justifyContent: "center",
							width: "22px",
							height: "22px",
							borderRadius: "5px",
							color: "#94a3b8",
							cursor: "pointer",
							transition: "background-color 0.15s, color 0.15s",
							"&:hover": { backgroundColor: "#f1f5f9", color: "#475569" },
						}}>
						<Download size={14} />
					</Box>
					{onToggleCollapse && (
						<Box
							onClick={onToggleCollapse}
							title={collapsed ? msg("expandSummary") : msg("collapseSummary")}
							sx={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								width: "22px",
								height: "22px",
								borderRadius: "5px",
								color: "#94a3b8",
								cursor: "pointer",
								transition: "background-color 0.15s, color 0.15s",
								"&:hover": { backgroundColor: "#f1f5f9", color: "#475569" },
							}}>
							{collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
						</Box>
					)}
				</HBox>
			</HBox>

			{!collapsed &&
				visible.map((status, idx) => {
					const bucket = summary[status];
					const vis = REPORT_STATUS_VISUALS[status];
					const currencies = Object.keys(bucket.currencies);
					return (
						<Fragment key={status}>
							{idx > 0 && <Box sx={{ height: "1px", background: "#eef2f6", margin: "0 -20px" }} />}
							<VBox sx={{ gap: "10px" }}>
								<HBox gap="8px" sx={{ alignItems: "center" }}>
									<Box
										sx={{
											display: "inline-flex",
											alignItems: "center",
											backgroundColor: vis.bg,
											color: vis.text,
											borderRadius: "6px",
											padding: "2px 8px",
											fontSize: "10px",
											fontWeight: 700,
											textTransform: "uppercase",
											letterSpacing: "0.05em",
										}}>
										{vis.label}
									</Box>
									<Typo sx={{ fontSize: "11px", color: "#94a3b8", fontVariantNumeric: "tabular-nums" }}>
										{msg("reportCount", { count: bucket.count, formatted: bucket.count.toLocaleString() })}
									</Typo>
									{bucket.from && bucket.to && (
										<Typo
											sx={{
												fontSize: "10px",
												color: "#cbd5e1",
												fontFamily:
													"ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
												fontVariantNumeric: "tabular-nums",
												whiteSpace: "nowrap",
											}}>
											{bucket.from === bucket.to ? bucket.from : `${bucket.from} → ${bucket.to}`}
										</Typo>
									)}
								</HBox>

								{currencies.map(cur => (
									<Box
										key={cur}
										sx={{
											display: "grid",
											gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
											gap: "12px 18px",
											alignItems: "stretch",
										}}>
										{REPORT_SUMMARY_METRICS.map(m => (
											<ReportSummaryItem
												key={m.key}
												label={m.label}
												amount={fmtSummaryMoney(bucket.currencies[cur][m.key])}
												currencyPrefix={cur}
												icon={m.icon}
												accent={m.accent}
												bg={m.bg}
											/>
										))}
									</Box>
								))}
							</VBox>
						</Fragment>
					);
				})}
		</VBox>
	);
};

// Thematic icons paired with each money column header. Helps scan the table by glyph
// rather than by reading label text — useful when columns are dense and shapes carry
// the meaning. Each path is a 24×24 viewBox.
const COLUMN_ICONS: Record<string, string> = {
	// trending-up arrow — money flowing through (turnover)
	total_amount: "M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z",
	// curved arrow back — refund / return
	total_refunds:
		"M12.5 8c-2.65 0-5.05.99-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z",
	// warning triangle — dispute / chargeback
	total_chargebacks: "M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z",
	// upward arrow out of tray — payout / OCT (money leaving)
	total_oct: "M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z",
	// closed lock — held / reserved funds (rolling)
	total_rollings:
		"M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6zm9 14H6V10h12v10z",
	// open lock — released funds
	total_release_rollings:
		"M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5-2.28 0-4.27 1.54-4.84 3.75-.14.54.18 1.08.72 1.22.53.14 1.08-.18 1.22-.72C9.44 3.93 10.63 3 12 3c1.66 0 3 1.34 3 3v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm0 12H6V10h12v10z",
	// percent sign — commission / fees
	total_fees:
		"M7.5 11c1.93 0 3.5-1.57 3.5-3.5S9.43 4 7.5 4 4 5.57 4 7.5 5.57 11 7.5 11zm0-5c.83 0 1.5.67 1.5 1.5S8.33 9 7.5 9 6 8.33 6 7.5 6.67 6 7.5 6zM4 17.5L19.5 2 21 3.5 5.5 19zM16.5 13c-1.93 0-3.5 1.57-3.5 3.5s1.57 3.5 3.5 3.5 3.5-1.57 3.5-3.5-1.57-3.5-3.5-3.5zm0 5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z",
	// wallet — net / final balance
	total_net:
		"M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z",
};

type TReportRow = {
	reportItem: TReportItem;
	client_name: string;
	date: string;
	status: string;
	currency: string;
	total_net: number;
	total_fees: number;
	total_amount: number;
	total_refunds: number;
	total_chargebacks: number;
	total_oct: number;
	total_rollings: number;
	total_ajustment_in: number;
	total_ajustment_out: number;
	total_release_rollings: number;
};

type TClientFilterItem = {
	id: number;
	name?: string | null;
	status?: string | null;
};

type TReportStatusFilterOption = {
	name: string;
	value: "" | "created" | "pending" | "payed";
};

const reportStatusOptions: TReportStatusFilterOption[] = [
	{ get name() { return msg("all"); }, value: "" },
	{ get name() { return msg("created"); }, value: "created" },
	{ get name() { return msg("pending"); }, value: "pending" },
	{ get name() { return msg("payed"); }, value: "payed" },
];

export interface IReportsTabProps {
	clientID?: number;
}

const hasDisplayValue = (value: string | number | null | undefined) => value !== null && value !== undefined && String(value).trim() !== "";

const normalizeClientFilterItems = (value: unknown): TClientFilterItem[] => {
	const items = Array.isArray(value)
		? value
		: value && typeof value === "object" && Array.isArray((value as { items?: unknown }).items)
			? (value as { items: unknown[] }).items
			: [];

	return items
		.filter((item): item is TClientFilterItem => typeof item === "object" && item !== null && typeof (item as TClientFilterItem).id === "number")
		.filter(item => item.id > 0 && item.status !== "deleted");
};

const getClientFilterLabel = (client?: TClientFilterItem) => {
	if (!client) return msg("allClients");
	if (hasDisplayValue(client.name)) return String(client.name);
	return msg("templateClient_2fb386d5", { v0: client.id });
};

const getClientStatusLabel = (status?: string | null) => {
	if (!hasDisplayValue(status)) return msg("unknown_b23a6a84");
	return String(status).replace(/_/g, " ");
};

const getClientStatusSx = (status?: string | null) => {
	const normalized = String(status ?? "").toLowerCase();
	if (normalized === "active") return { color: "#166534", backgroundColor: "#dcfce7", border: "1px solid #bbf7d0" };
	if (normalized === "blocked" || normalized === "disabled" || normalized === "deleted")
		return { color: "#991b1b", backgroundColor: "#fee2e2", border: "1px solid #fecaca" };
	if (normalized === "pending" || normalized === "created") return { color: "#92400e", backgroundColor: "#fef3c7", border: "1px solid #fde68a" };
	return { color: "#334155", backgroundColor: "#e2e8f0", border: "1px solid #cbd5e1" };
};

const sortClientFilterItems = (items: TClientFilterItem[]) =>
	[...items].sort((a, b) => {
		const aActive = String(a.status ?? "").toLowerCase() === "active";
		const bActive = String(b.status ?? "").toLowerCase() === "active";
		if (aActive !== bActive) return aActive ? -1 : 1;
		return getClientFilterLabel(a).localeCompare(getClientFilterLabel(b));
	});

const ClientFilterControl = ({ client, onClick }: { client?: TClientFilterItem; onClick: () => void }) => { useLocale(); return ((
	<Box sx={{ position: "relative", minWidth: "190px", maxWidth: "260px" }}>
		<TypoCaption block>{msg("client_b886ce22")}</TypoCaption>
		<Spacer size="3px" />
		<HBox
			onClick={event => {
				event?.preventDefault();
				event?.stopPropagation();
				onClick();
			}}
			sx={{
				alignItems: "center",
				minWidth: 0,
				...InputBoxStyle(false),
				borderRadius: "4px",
				cursor: "pointer",
				overflow: "hidden",
				position: "relative",
			}}>
			<Box sx={{ ...InputFontStyle("small"), overflow: "hidden", flex: "1 1 auto", minWidth: 0 }}>
				<Typo sx={{ textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", color: client ? "#334155" : "#94a3b8" }}>
					{getClientFilterLabel(client)}
				</Typo>
			</Box>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					cursor: "pointer",
					flexShrink: 0,
					marginLeft: "auto",
					width: "30px",
					"&>svg": { pointerEvents: "none", height: "24px", width: "24px" },
				}}>
				<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M17 9V11L12 16L7 11V9L12 14L17 9Z" fill="#000000CC" fillOpacity="1" />
				</svg>
			</Box>
		</HBox>
	</Box>
)); };

const ClientFilterPopup = ({
	open,
	items,
	busy,
	error,
	search,
	selectedClient,
	onSearchChange,
	onSelect,
	onClose,
}: {
	open: boolean;
	items: TClientFilterItem[];
	busy: boolean;
	error?: string;
	search: { search: string };
	selectedClient?: TClientFilterItem;
	onSearchChange: (value: string) => void;
	onSelect: (client?: TClientFilterItem) => void;
	onClose: () => void;
}) => {
	useLocale();
	if (!open) return null;

	const searchValue = search.search.trim().toLowerCase();
	const sortedItems = sortClientFilterItems(items);
	const filteredItems = searchValue
		? sortedItems.filter(client => {
				const haystack = [client.name, client.status, client.id].filter(hasDisplayValue).join(" ").toLowerCase();
				return haystack.includes(searchValue);
			})
		: sortedItems;

	return createPortal(
		<Box
			onClick={onClose}
			sx={{
				position: "fixed",
				inset: 0,
				zIndex: 10000,
				backgroundColor: "rgba(15,23,42,0.22)",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "20px",
				boxSizing: "border-box",
			}}>
			<VBox
				onClick={event => event?.stopPropagation()}
				sx={{
					width: "min(520px, 100%)",
					maxHeight: "75vh",
					backgroundColor: "#fff",
					borderRadius: "14px",
					boxShadow: "0 22px 60px rgba(15,23,42,0.28), 0 6px 20px rgba(15,23,42,0.16)",
					overflow: "hidden",
				}}>
				<HBox sx={{ alignItems: "center", gap: "10px", padding: "16px 18px", borderBottom: "1px solid #f1f5f9" }}>
					<Typo variant="medium" bold sx={{ fontSize: "15px", color: "#1e293b" }}>{msg("selectClient")}</Typo>
					<Spacer grow />
					<Button onClick={onClose} title={msg("close")} variant="secondarySmall" />
				</HBox>

				<VBox sx={{ padding: "14px 18px", gap: "12px", minHeight: 0 }}>
					<Input size="small" type="search" placeholder={msg("search_7f553822")} name="search" onChange={onSearchChange} data={search} />

					<VBox sx={{ overflow: "auto", maxHeight: "52vh", gap: "6px" }}>
						<HBox
							onClick={() => onSelect(undefined)}
							sx={{
								padding: "10px 12px",
								border: "1px solid #cbd5e1",
								borderRadius: "8px",
								cursor: "pointer",
								backgroundColor: !selectedClient ? "#eef4ff" : "#fff",
								"&:hover": { backgroundColor: "#f1f5f9" },
							}}>
							<Typo variant="medium" sx={{ color: "#1e293b", fontSize: "13px", fontWeight: 600 }}>{msg("allClients")}</Typo>
						</HBox>

						{busy && (
							<Typo variant="medium" sx={{ color: "#94a3b8", padding: "12px" }}>{msg("loading")}</Typo>
						)}

						{error && !busy && (
							<Typo variant="medium" sx={{ color: "#dc2626", padding: "12px" }}>
								{error}
							</Typo>
						)}

						{!busy && !error && filteredItems.length === 0 && (
							<Typo variant="medium" sx={{ color: "#94a3b8", padding: "12px" }}>{msg("noClients")}</Typo>
						)}

						{!busy &&
							!error &&
							filteredItems.map(client => (
								<HBox
									key={client.id}
									onClick={() => onSelect(client)}
									sx={{
										padding: "10px 12px",
										border: "1px solid #cbd5e1",
										borderRadius: "8px",
										cursor: "pointer",
										alignItems: "center",
										gap: "12px",
										backgroundColor: selectedClient?.id === client.id ? "#eef4ff" : "#fff",
										transition: "background-color 0.15s, border-color 0.15s",
										"&:hover": { backgroundColor: "#f8fafc", borderColor: "#cbd5e1" },
									}}>
									<VBox sx={{ overflow: "hidden", gap: "2px" }}>
										<Typo
											variant="medium"
											sx={{
												overflow: "hidden",
												textOverflow: "ellipsis",
												whiteSpace: "nowrap",
												fontSize: "13px",
												color: "#0f172a",
												fontWeight: 600,
											}}>
											{getClientFilterLabel(client)}
										</Typo>
									</VBox>
									<Spacer grow />
									<Typo
										variant="caption"
										sx={{
											flexShrink: 0,
											borderRadius: "6px",
											padding: "4px 8px",
											fontSize: "11px",
											textTransform: "capitalize",
											whiteSpace: "nowrap",
											...getClientStatusSx(client.status),
										}}>
										{getClientStatusLabel(client.status)}
									</Typo>
									<Typo
										variant="caption"
										sx={{
											flexShrink: 0,
											color: "#334155",
											backgroundColor: "#e2e8f0",
											borderRadius: "6px",
											padding: "4px 8px",
											fontSize: "11px",
											fontVariantNumeric: "tabular-nums",
										}}>
										#{client.id}
									</Typo>
								</HBox>
							))}
					</VBox>
				</VBox>
			</VBox>
		</Box>,
		document.body,
	);
};

export const ReportsTab = ({ clientID }: IReportsTabProps) => {
	useLocale();
	const [project] = GD.B_SELECTED_PROJECT.use();
	const appliedFilters = useRef<ReportsExportFilters>({ ...(clientID ? { client_ids: [clientID] } : {}) });
	const exportVersion = useRef(0);
	const exportPending = useRef(false);
	const [exportBusy, setExportBusy] = useState(false);
	const [exportProgress, setExportProgress] = useState("");
	const [fromDate, setFromDate] = useState<string>("");
	const [toDate, setToDate] = useState<string>("");
	const [, setCurrentPage] = useState(0);
	const [clientFilter, setClientFilter] = useState<TClientFilterItem | undefined>();
	const [clientFilterItems, setClientFilterItems] = useState<TClientFilterItem[]>([]);
	const [clientFilterOpen, setClientFilterOpen] = useState(false);
	const [clientFilterBusy, setClientFilterBusy] = useState(false);
	const [clientFilterError, setClientFilterError] = useState<string | undefined>();
	const [clientSearch, setClientSearch] = useState<{ search: string }>({ search: "" });
	const [statusFilter, setStatusFilter] = useState<TReportStatusFilterOption>(reportStatusOptions[0]);
	const [summaryCollapsed, setSummaryCollapsed] = useState(false);
	const scrollRef = useRef<HTMLDivElement>(null);
	const [headerHeight] = GD.B_HEADER_HEIGHT.use(undefined, undefined, []);
	const [report, loadReport, reportBusy] = useAPI<{ rows: TReportItem[]; total: number; page: number; perPage: number; summary?: TReportsSummary | null }>(
		null,
	);

	const doLoad = (page = 1, filters?: ReportsExportFilters) => {
		if (!filters) {
			filters = {
				...(fromDate ? { from_date: fromDate } : {}),
				...(toDate ? { to_date: toDate } : {}),
				...(clientID || clientFilter?.id ? { client_ids: [clientID || clientFilter!.id] } : {}),
				...(statusFilter.value ? { status: statusFilter.value } : {}),
			};
			appliedFilters.current = filters;
		}
		loadReport({ method: "reports/get", data: { ...filters, page, per_page: 100 } });
	};

	useEffect(() => {
		exportVersion.current++;
		exportPending.current = false;
		setExportBusy(false);
		return () => { exportVersion.current++; };
	}, [clientID, project?.id]);

	const downloadCsv = async () => {
		if (exportPending.current || !project) return;
		exportPending.current = true;
		const version = ++exportVersion.current;
		const filters = { ...appliedFilters.current };
		const projectId = project.id;
		setExportBusy(true);
		setExportProgress("");
		const checkContext = async () => {
			const current = await GD.REQ_CURRENT_PROJECT.request();
			if (version !== exportVersion.current || current?.id !== projectId) throw new Error(msg("projectChangedStartTheDownloadAgain"));
		};
		try {
			const reports = await loadReportsForCsv(async page => {
				await checkContext();
				const response = await GD.REQ_API.request({
					method: "reports/get",
					data: { ...filters, page, per_page: 1000 },
					expectedProjectId: projectId,
				});
				await checkContext();
				if (response.error) throw new Error(response.error.message || msg("couldNotDownloadReports"));
				return response.data;
			}, (loaded, total) => setExportProgress(`${loaded}/${total}`));
			await checkContext();
			if (!reports.length) { notify.info(msg("noReportsToDownloadForTheseFilters")); return; }
			const filename = `reports_${filters.client_ids?.[0] ? `client_${filters.client_ids[0]}` : `project_${projectId}`}_${filters.from_date || "default"}_${filters.to_date || "latest"}_${filters.status || "all"}.csv`;
			saveAs(new Blob([buildReportsCsv(reports)], { type: "text/csv;charset=utf-8" }), filename);
		} catch (error) {
			if (version === exportVersion.current) notify.error(getNotificationErrorMessage(error, msg("couldNotDownloadReports")));
		} finally {
			if (version === exportVersion.current) { exportPending.current = false; setExportBusy(false); }
		}
	};

	useEffect(() => {
		doLoad();
	}, [clientID]);

	const reportData = report && "data" in report ? report.data : null;
	const reportItems: TReportItem[] = reportData?.rows ?? [];
	const summary = reportData?.summary;

	const paginationData: IPagination | undefined = reportData
		? { total: reportData.total, page: reportData.page - 1, perPage: reportData.perPage, totalPages: Math.ceil(reportData.total / reportData.perPage) }
		: undefined;

	const rows: TReportRow[] = reportItems.flatMap(item => {
		const totals = item.data?.totals;
		const clientName = item.data?.name ?? "—";
		if (!totals || Object.keys(totals).length === 0) {
			return [
				{
					reportItem: item,
					client_name: clientName,
					date: formatDisplayDate(item.date),
					status: item.status ?? "—",
					currency: "—",
					total_net: NaN,
					total_fees: NaN,
					total_amount: NaN,
					total_refunds: NaN,
					total_chargebacks: NaN,
					total_oct: NaN,
					total_rollings: NaN,
					total_ajustment_in: NaN,
					total_ajustment_out: NaN,
					total_release_rollings: NaN,
				},
			];
		}
		return Object.entries(totals).map(([currency, t]) => ({
			reportItem: item,
			client_name: clientName,
			date: formatDisplayDate(item.date),
			status: item.status ?? "—",
			currency,
			total_net: t.total_net ?? 0,
			total_fees: t.total_fees ?? 0,
			total_amount: t.total_amount ?? 0,
			total_refunds: t.total_refunds ?? 0,
			total_chargebacks: t.total_chargebacks ?? 0,
			total_oct: t.total_oct ?? 0,
			total_rollings: t.total_rollings ?? 0,
			total_ajustment_in: t.total_ajustment_in ?? 0,
			total_ajustment_out: t.total_ajustment_out ?? 0,
			total_release_rollings: t.total_release_rollings ?? 0,
		}));
	});

	const noData = !reportBusy && rows.length === 0;
	const showClientFilter = !clientID;

	const stickyHeadSx = {
		position: "relative" as const,
		zIndex: 2,
		backgroundColor: "#e3dfd6",
		borderTop: "20px solid #f8fafc",
	};

	const stickyHeadRightSx = {
		...stickyHeadSx,
		textAlign: "right" as const,
	};
	// "_spacer" is a phantom column that absorbs leftover horizontal space — it sits between
	// the text-anchored columns on the left and the numeric block on the right, so the
	// numbers stay flush to the right edge regardless of viewport width.
	const tableHeaders = [
		...(showClientFilter ? [{ value: "client_name", gridColumn: "minmax(140px, max-content)", sx: stickyHeadSx }] : []),
		{ value: "date", gridColumn: "minmax(90px, max-content)", sx: stickyHeadSx },
		{ value: "status", gridColumn: "minmax(80px, max-content)", sx: stickyHeadSx },
		{ value: "_spacer", gridColumn: "1fr", sx: stickyHeadSx },
		{ value: "currency", gridColumn: "minmax(60px, max-content)", sx: stickyHeadRightSx },
		{ value: "total_amount", gridColumn: "minmax(90px, max-content)", sx: stickyHeadRightSx },
		{ value: "total_refunds", gridColumn: "minmax(80px, max-content)", sx: stickyHeadRightSx },
		{ value: "total_chargebacks", gridColumn: "minmax(90px, max-content)", sx: stickyHeadRightSx },
		{ value: "total_oct", gridColumn: "minmax(80px, max-content)", sx: stickyHeadRightSx },
		{ value: "total_rollings", gridColumn: "minmax(80px, max-content)", sx: stickyHeadRightSx },
		{ value: "total_release_rollings", gridColumn: "minmax(90px, max-content)", sx: stickyHeadRightSx },
		{ value: "total_fees", gridColumn: "minmax(80px, max-content)", sx: stickyHeadRightSx },
		{ value: "total_net", gridColumn: "minmax(100px, max-content)", sx: stickyHeadRightSx },
	];
	const tableOrder = tableHeaders.map(header => header.value) as (keyof TReportRow)[];
	const gridTemplateColumns = tableHeaders.map(h => h.gridColumn).join(" ");

	useEffect(() => {
		const wrapper = scrollRef.current;
		if (!wrapper || rows.length === 0) return;

		const grid = wrapper.firstElementChild as HTMLElement;
		if (!grid) return;

		const headerCells = Array.from(grid.children).slice(0, tableHeaders.length) as HTMLElement[];
		if (headerCells.length === 0) return;

		const stickyTop = headerHeight ?? 0;

		const handleScroll = () => {
			const wrapperRect = wrapper.getBoundingClientRect();
			const offset = stickyTop - wrapperRect.top;
			const maxOffset = grid.offsetHeight - headerCells[0].offsetHeight;
			const clamped = Math.max(0, Math.min(offset, maxOffset));

			for (const cell of headerCells) {
				cell.style.transform = clamped > 0 ? `translateY(${clamped}px)` : "";
			}
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		handleScroll();

		return () => window.removeEventListener("scroll", handleScroll);
	}, [rows, headerHeight]);

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		doLoad(page + 1, appliedFilters.current);
	};

	const openClientFilter = async () => {
		setClientFilterOpen(true);
		setClientSearch({ search: "" });
		setClientFilterBusy(true);
		setClientFilterError(undefined);

		try {
			const res = await GD.REQ_CLIENTS.request({});

			if (res?.error) {
				setClientFilterError(res.error.message ?? msg("unableToLoadClients"));
				setClientFilterItems([]);
				return;
			}

			setClientFilterItems(normalizeClientFilterItems(res?.data));
		} finally {
			setClientFilterBusy(false);
		}
	};

	const selectClientFilter = (client?: TClientFilterItem) => {
		setClientFilter(client);
		setClientFilterOpen(false);
		setCurrentPage(0);
	};

	return (
		<>
			<ProgressBar busy={reportBusy} />
			<HBox gap="8px" sx={{ alignItems: "end", flexWrap: "wrap" }}>
				<DateRangePicker
					style={{ width: "290px", maxWidth: "100%" }}
					label={msg("dateRange")}
					from={fromDate}
					to={toDate}
					allowClear
					onChange={(from, to) => { setFromDate(from); setToDate(to); }}
				/>
				<Select<TReportStatusFilterOption>
					sx={{ minWidth: "150px", maxWidth: "190px" }}
					size="small"
					label={msg("status")}
					value={statusFilter}
					values={reportStatusOptions}
					onChange={value =>
						setStatusFilter(reportStatusOptions.find(option => option.value === (value as unknown as string)) ?? reportStatusOptions[0])
					}
				/>
				{showClientFilter && <ClientFilterControl client={clientFilter} onClick={openClientFilter} />}
				<Button
					size="small"
					title={msg("show")}
					variant="primarySmall"
					disabled={exportBusy || reportBusy}
					onClick={() => {
						setCurrentPage(0);
						doLoad(1);
					}}
				/>
				<Button
					title={exportBusy ? msg("templateDownloading_e9abfe7d", { v0: exportProgress ? ` ${exportProgress}` : "..." }) : msg("downloadCsv")}
					variant="secondarySmall"
					icon={<Download size={15} />}
					sx={{ marginLeft: "auto", flexShrink: 0 }}
					disabled={exportBusy || reportBusy || !project || !!report?.error || !reportItems.length}
					onClick={downloadCsv}
				/>
			</HBox>
			<ClientFilterPopup
				open={clientFilterOpen}
				items={clientFilterItems}
				busy={clientFilterBusy}
				error={clientFilterError}
				search={clientSearch}
				selectedClient={clientFilter}
				onSearchChange={value => setClientSearch({ search: value })}
				onSelect={selectClientFilter}
				onClose={() => setClientFilterOpen(false)}
			/>
			{summary && (
				<ReportsSummaryPanel
					summary={summary}
					from={fromDate}
					to={toDate}
					collapsed={summaryCollapsed}
					onToggleCollapse={() => setSummaryCollapsed(c => !c)}
				/>
			)}
			{reportData && summary === null && <ReportsSummaryHint />}
			{noData && (
				<>
					<Spacer size="10px" />
					<NoRecords line1={msg("ui_0067dd1035")} line2={msg("ui_9d44eb322d")} />
				</>
			)}
			{rows.length > 0 && (
				<Box sx={{ flex: "1 1 100%", minWidth: 0, boxSizing: "border-box" }}>
					<div ref={scrollRef} style={{ overflowX: "auto" }}>
						<Table<TReportRow>
							headPadding="14px 12px"
							cellPadding="8px 12px"
							sx={{ width: "100%" }}
							data={rows}
							onRowClick={row => {
								S_REPORT_POPUP.invoke({
									id: row.reportItem.id,
									date: row.reportItem.date,
								});
							}}
							headers={tableHeaders}
							order={tableOrder}
							gridTemplateColumns={gridTemplateColumns}
							headerFactory={val => {
								if (val === "_spacer") return { content: <></> };
								if (typeof val === "string") {
									const rawLabel = val === "total_amount" ? "Turnover" : val.replace(/^total_/i, "");
									const iconPath = COLUMN_ICONS[val];
									const labelText = rawLabel.replace(/_/g, " ").toUpperCase();
									return {
										content: (
											<Typo
												variant="medium"
												sx={{
													fontWeight: 400,
													fontSize: "11px",
													fontFamily:
														"ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
													textTransform: "uppercase",
													letterSpacing: "0.06em",
													whiteSpace: "nowrap",
													color: "#000",
												}}>
												{iconPath && (
													<svg
														width="13"
														height="13"
														viewBox="0 0 24 24"
														fill="#64748b"
														aria-hidden
														style={{ verticalAlign: "-2px", marginRight: "5px" }}>
														<path d={iconPath} />
													</svg>
												)}
												{labelText}
											</Typo>
										),
									};
								}
								return { content: null };
							}}
							cellFactory={(row, cell, _index, _key, name) => {
								if (name === "_spacer") return { content: <></> };
								if (name === "date") {
									const formatted = formatDisplayDate(row.reportItem.date, { format: { day: "2-digit", month: "2-digit", year: "2-digit" }, fallback: String(cell) });
									return {
										content: (
											<Typo sx={{ fontSize: "13px", whiteSpace: "nowrap", color: "#1e293b" }} variant="medium">
												<DateTimeTooltip value={row.reportItem.date}>{formatted}</DateTimeTooltip>
											</Typo>
										),
									};
								}
								if (name === "status") {
									const s = String(cell || "").toLowerCase() as TReportStatus;
									const v = REPORT_STATUS_VISUALS[s] ?? { bg: "#f1f5f9", text: "#475569", label: s || "—" };
									return {
										content: (
											<Box
												sx={{
													display: "inline-flex",
													alignItems: "center",
													padding: "1px 7px",
													borderRadius: "4px",
													backgroundColor: v.bg,
													color: v.text,
													lineHeight: "16px",
													whiteSpace: "nowrap",
												}}>
												<span style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.1px" }}>{v.label}</span>
											</Box>
										),
									};
								}
								if (name === "currency") {
									return {
										sx: { textAlign: "right" as const },
										content: (
											<Typo sx={{ fontSize: "13px", whiteSpace: "nowrap", color: "#475569" }} variant="medium">
												{cell}
											</Typo>
										),
									};
								}
								if (name === "client_name") {
									return {
										content: (
											<Typo
												sx={{
													fontSize: "13px",
													whiteSpace: "nowrap",
													overflow: "hidden",
													textOverflow: "ellipsis",
													color: "#1e293b",
													fontWeight: 600,
												}}
												variant="medium">
												{cell}
											</Typo>
										),
									};
								}
								const val = cell as number;
								const isNet = name === "total_net";
								const isEmpty = isNaN(val) || val === 0;
								const isNegative = !isEmpty && val < 0;
								const color = isEmpty ? "#cbd5e1" : isNegative ? "#dc2626" : isNet ? "#15803d" : "#1e293b";
								return {
									sx: { textAlign: "right" as const },
									content: (
										<Typo
											variant="medium"
											sx={{
												whiteSpace: "nowrap",
												fontVariantNumeric: "tabular-nums",
												fontSize: "13px",
												fontWeight: (isNet || isNegative) && !isEmpty ? 600 : 400,
												color,
											}}>
											{isNaN(val) ? "—" : formatAmount(val)}
										</Typo>
									),
								};
							}}
						/>
					</div>
				</Box>
			)}
			<div style={{ flexGrow: 1 }} />
			<div style={{ position: "sticky", bottom: 0, backgroundColor: "#f8fafc", zIndex: 3, paddingTop: "8px", paddingBottom: "8px" }}>
				<Pagination pagination={paginationData} onPageChange={handlePageChange} />
			</div>
		</>
	);
};
